import { MedicationPrescription } from '../api-client.service';
// Site no.;Practice Nr;Y Numbers;Store Name;Store Address;Town;Postal Code;Province;lat;lng;Trading Hours;Telephone No.;Dispensary No.;email;Column2;
export interface AuthResponse {
  access_token: string;
  expires_in: number;
  token_type: string;
  scope: string;
  refresh_token: string;
}
export interface DischemPharmacy {
  isFavourite: boolean;
  siteNo: string;
  practiceNo: string;
  yNumbers: string;
  storeName: string;
  storeAddress: string;
  town: string;
  postalCode: string;
  province: string;
  lat?: string;
  lng?: string;
  tradingHours: string;
  telephoneNo: string;
  dispensaryNo: string;
  email: string;
}

export interface DischemOrder {
  originating_system_name: string;
  message_id: string;
  timestamp: string;
  order_number: string;
  reference_order_number: string;
  entry_status_code: number;
  rx_number: string;
  process_date_time: string;
  store_code: string;
  order_type: string;
  main_member_details: MemberDetails;
  dependant_details: DependantDetails;
  recipient_details: RecipientDetails;
  delivery_address_details: AddressDetails;
  dispensed_patient: string;
  payment_type: string;
  comments: string;
  original_script_image: string;
  allergies: string;
  generics: string;
  repeat_script: string;
  fridge_item_type: string;
  special_instructions: string;
  amount_due: string;
  amount_paid: string;
  longitude: number;
  latitude: number;
  delivery_date_time: string;
  is_whatsapp: string;
  order_source: string;
  urgent: string;
}

export interface MemberDetails {
  surname: string;
  firstname: string;
  initials: string;
  title: string;
  dob: string;
  medical_aid: string;
  medical_aid_no: string;
  id_number: string;
  gender: string;
  language: string;
  home_tel_no: string;
  business_tel_no: string;
  cell_no: string;
  email: string;
  fax: string;
  address_details: AddressDetails;
  postal_details: PostalDetails;
  doctor_details: DoctorDetails;
  loyalty_card_number: string;
}

export interface DependantDetails {
  dependant_number: string;
  surname: string;
  firstname: string;
  initials: string;
  title: string;
  relation: string;
  dob: string;
  medical_aid: string;
  medical_aid_no: string;
  id_number: string;
  gender: string;
  language: string;
  home_tel_no: string;
  business_tel_no: string;
  cell_no: string;
  email: string;
  fax: string;
  address_details: AddressDetails;
  postal_details: PostalDetails;
  doctor_details: DoctorDetails;
}

export interface RecipientDetails {
  contact_number: string;
  name: string;
  surname: string;
}

export interface AddressDetails {
  line_one: string;
  line_two: string;
  line_three: string;
  area_code: string;
}

export interface PostalDetails {
  line_one: string;
  line_two: string;
  line_three: string;
  postal_code: string;
}

export interface DoctorDetails {
  name: string;
  practice_number: string;
  contact_number: string;
}

export interface MedLinesRequest {
  order_number: string;
  originating_system_name: string;
  message_id: string;
  timestamp: string;
  med_lines: MedLine[];
}

export interface MedLine {
  item_line_no: string;
  drug_name: string;
  nappi_code: string;
  dosage: string;
  strength: string;
  dispense_qty: number;
  repeat_number: number;
  days_supply: number;
  prim_icd10_code: string;
  seco_icd10_code: string;
  directions: Directions;
  value: number;
  repeat_total: number;
}

export interface Directions {
  line_one: string;
  line_two: string;
}

export interface OrderStatusRequest {
  order_number: string;
  originating_system_name: string;
  message_id: string;
  timestamp: string;
  status: number;
}

export interface OrderResponse {
  success: boolean;
  sender_id: string;
  message_id: string;
  order_number: string;
  timestamp: string;
  status: string;
  message: string;
}

export interface MedLinesResponse {
  success: boolean;
  sender_id: string;
  message_id: string;
  order_number: string;
  timestamp: string;
  status: string;
  message: string;
}

export interface DischemScriptRequest {
  prescription?: MedicationPrescription;
  scriptPdfBase64?: string;
  pharmacy?: DischemPharmacy;
  practiceTenantId?: string;
  encounterId?: string;
  patientId?: string;
}

// --- Status Codes ---

export enum DischemOrderStatusCode {
  OrderReceived = 1,
  OrderConfirmed = 2,
  OrderCancelled = 3,
  OrderDispensed = 4,
  OrderDispatched = 5,
  OrderDelivered = 6,
  OrderReturned = 7,
  OrderRedelivery = 8,
  OrderRedispatched = 9,
  OnlineReadyToCollect = 10,
  OnlineReadyToDispatch = 11,
  DelayedOrder = 14,
  OrderReadyToCollect = 18,
  OrderCollected = 19,
  ReadyToDispatch = 20,
  ReadyToDispatchInStore = 21,
  AwaitingPayment = 22,
  PaymentReceived = 23,
  OnRouteToStore = 24,
}

export const DISCHEM_STATUS_LABELS: Record<DischemOrderStatusCode, string> = {
  [DischemOrderStatusCode.OrderReceived]: 'Order Received',
  [DischemOrderStatusCode.OrderConfirmed]: 'Order Confirmed',
  [DischemOrderStatusCode.OrderCancelled]: 'Order Cancelled',
  [DischemOrderStatusCode.OrderDispensed]: 'Order Dispensed',
  [DischemOrderStatusCode.OrderDispatched]: 'Order Dispatched',
  [DischemOrderStatusCode.OrderDelivered]: 'Order Delivered',
  [DischemOrderStatusCode.OrderReturned]: 'Order Returned',
  [DischemOrderStatusCode.OrderRedelivery]: 'Order Re-delivery',
  [DischemOrderStatusCode.OrderRedispatched]: 'Order Re-dispatched',
  [DischemOrderStatusCode.OnlineReadyToCollect]: 'Online Ready to Collect',
  [DischemOrderStatusCode.OnlineReadyToDispatch]: 'Online Ready to Dispatch',
  [DischemOrderStatusCode.DelayedOrder]: 'Delayed Order',
  [DischemOrderStatusCode.OrderReadyToCollect]: 'Order Ready to Collect',
  [DischemOrderStatusCode.OrderCollected]: 'Order Collected',
  [DischemOrderStatusCode.ReadyToDispatch]: 'Ready to Dispatch',
  [DischemOrderStatusCode.ReadyToDispatchInStore]: 'Ready to Dispatch (In-Store)',
  [DischemOrderStatusCode.AwaitingPayment]: 'Awaiting Payment',
  [DischemOrderStatusCode.PaymentReceived]: 'Payment Received',
  [DischemOrderStatusCode.OnRouteToStore]: 'On Route to Store',
};

export const DISCHEM_CANCELLABLE_CODES: DischemOrderStatusCode[] = [
  DischemOrderStatusCode.OrderReceived, // 1
  DischemOrderStatusCode.OrderConfirmed, // 2
  DischemOrderStatusCode.OrderDispensed, // 4 — dispensed but not yet collected
  DischemOrderStatusCode.OrderDispatched, // 5 — in transit (delivery flow)
  DischemOrderStatusCode.OrderReturned, // 7 — recovery state, still cancellable
  DischemOrderStatusCode.OrderRedelivery, // 8 — scheduled re-delivery, can be cancelled
  DischemOrderStatusCode.OrderRedispatched, // 9 — recovery state, still cancellable
  DischemOrderStatusCode.OnlineReadyToCollect, // 10 — not yet picked up
  DischemOrderStatusCode.DelayedOrder, // 14
  // Excluded: 6 (OrderDelivered) — patient already has the medication
];

export const DISCHEM_DELIVERED_CODES: DischemOrderStatusCode[] = [
  DischemOrderStatusCode.OrderDelivered,
  DischemOrderStatusCode.OrderCollected,
];

// Shape written by webhook into Firestore
export interface DischemOrderStatusRecord {
  statusCode: number;
  statusUpdatedAt: string;
  statusHistory: Array<{ statusCode: number; timestamp: string }>;
}

/**
 * Status 1 is written locally after a successful send; the top-level DischemOrders doc may not
 * exist yet when the user opens tracking — use this so the dialog matches the row pill default.
 */
export function createDefaultDischemOrderStatusRecord(sentAt?: string | null): DischemOrderStatusRecord {
  const ts = (sentAt && String(sentAt).trim()) || new Date().toISOString();
  return {
    statusCode: DischemOrderStatusCode.OrderReceived,
    statusUpdatedAt: ts,
    statusHistory: [{ statusCode: DischemOrderStatusCode.OrderReceived, timestamp: ts }],
  };
}

// For the timeline UI
export type DischemTimelineStepState = 'completed' | 'active' | 'pending' | 'cancelled';

export interface DischemTimelineStep {
  statusCode: number;
  label: string;
  icon: string;
  description: string;
  state: DischemTimelineStepState;
  timestamp?: string;
}

const DISCHEM_STATUS_META: Partial<Record<number, { icon: string; description: string }>> = {
  [DischemOrderStatusCode.OrderReceived]: {
    icon: 'inbox',
    description: 'Prescription submitted and received by Dis-Chem. Order is unconfirmed.',
  },
  [DischemOrderStatusCode.OrderConfirmed]: { icon: 'check_circle', description: 'Order confirmed and entered into the dispensing queue.' },
  [DischemOrderStatusCode.OrderCancelled]: { icon: 'cancel', description: 'The order has been cancelled.' },
  [DischemOrderStatusCode.OrderDispensed]: { icon: 'medication', description: 'Medication dispensed and packaged at the Dis-Chem store.' },
  [DischemOrderStatusCode.OrderDispatched]: { icon: 'local_shipping', description: 'Order handed to courier / out for delivery.' },
  [DischemOrderStatusCode.OrderDelivered]: { icon: 'home', description: 'Successfully delivered to the patient.' },
  [DischemOrderStatusCode.OrderReturned]: { icon: 'undo', description: 'Order has been returned.' },
  [DischemOrderStatusCode.OrderRedelivery]: { icon: 'replay', description: 'Order scheduled for re-delivery.' },
  [DischemOrderStatusCode.OrderRedispatched]: { icon: 'local_shipping', description: 'Order re-dispatched to courier.' },
  [DischemOrderStatusCode.OnlineReadyToCollect]: { icon: 'store', description: 'Order ready for online collection.' },
  [DischemOrderStatusCode.OnlineReadyToDispatch]: { icon: 'inventory_2', description: 'Order ready to dispatch online.' },
  [DischemOrderStatusCode.DelayedOrder]: { icon: 'schedule', description: 'Order is delayed.' },
  [DischemOrderStatusCode.OrderReadyToCollect]: { icon: 'store', description: 'Order is ready to collect at the store.' },
  [DischemOrderStatusCode.OrderCollected]: { icon: 'done_all', description: 'Order successfully collected.' },
  [DischemOrderStatusCode.ReadyToDispatch]: { icon: 'inventory_2', description: 'Package prepared and ready for courier handover.' },
  [DischemOrderStatusCode.ReadyToDispatchInStore]: { icon: 'inventory_2', description: 'Package ready to dispatch from store.' },
  [DischemOrderStatusCode.AwaitingPayment]: {
    icon: 'credit_card',
    description: 'Patient payment confirmation is pending before dispensing begins.',
  },
  [DischemOrderStatusCode.PaymentReceived]: { icon: 'payments', description: 'Payment successfully processed; order moves to dispensing.' },
  [DischemOrderStatusCode.OnRouteToStore]: {
    icon: 'route',
    description: 'Package in transit to the destination store or patient address.',
  },
};

export function buildDischemTimelineSteps(statusRecord: DischemOrderStatusRecord): DischemTimelineStep[] {
  const { statusCode: current = 1, statusHistory = [], statusUpdatedAt } = statusRecord;
  const historyMap = new Map(statusHistory.map(h => [h.statusCode, h.timestamp]));

  const codes = [...statusHistory.map(h => h.statusCode)];
  if (!historyMap.has(current)) codes.push(current);

  // Terminal "done" statuses: the journey is complete, show as completed (green), not active (orange/pulsing)
  const isJourneyComplete = DISCHEM_DELIVERED_CODES.includes(current);

  return codes.map(code => {
    const meta = DISCHEM_STATUS_META[code];
    const isCurrent = code === current;
    const isCancelled = code === DischemOrderStatusCode.OrderCancelled;

    let state: DischemTimelineStepState;
    if (isCancelled) {
      state = 'cancelled';
    } else if (isCurrent && isJourneyComplete) {
      state = 'completed'; // Order collected/delivered — fully done, not "in progress"
    } else if (isCurrent) {
      state = 'active';
    } else {
      state = 'completed';
    }

    return {
      statusCode: code,
      label: DISCHEM_STATUS_LABELS[code] ?? `Status ${code}`,
      icon: meta?.icon ?? 'help_outline',
      description: meta?.description ?? '',
      state,
      timestamp: historyMap.get(code) ?? (isCurrent ? statusUpdatedAt : undefined),
    };
  });
}
